/**
 * MAHI — top-level router. Mounted at /mira/* from the main App.
 *
 * Only two things ever gate a route: having a token at all, and — for a
 * brand-new account — having finished the one-screen Welcome step (name +
 * gender). Everything else (email, photo, style picks) is reachable from
 * Profile and never redirects anyone.
 */
import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MiraAuthProvider, useMiraAuth } from './auth';
import MiraAuth from './pages/Auth';
import MiraWelcome from './pages/Welcome';
import MiraProfile from './pages/Profile';
import MiraHome from './pages/Home';
import MiraRobot from './components/MiraRobot';
import MiraShell from './components/MiraShell';

const Loading = () => (
  <MiraShell>
    <div className="min-h-screen flex items-center justify-center"><MiraRobot size={84} /></div>
  </MiraShell>
);

const Guarded = ({ children, requireBasicInfo = true }) => {
  const { user, loading } = useMiraAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/mira/login" replace />;
  const hasBasicInfo = Boolean(user.name && user.gender);
  if (requireBasicInfo && !hasBasicInfo && location.pathname !== '/mira/welcome') {
    return <Navigate to="/mira/welcome" replace />;
  }
  return children;
};

const MiraApp = () => (
  <MiraAuthProvider>
    <Routes>
      <Route path="login" element={<MiraAuth />} />
      <Route path="welcome" element={<Guarded requireBasicInfo={false}><MiraWelcome /></Guarded>} />
      <Route path="home" element={<Guarded><MiraHome /></Guarded>} />
      <Route path="profile" element={<Guarded><MiraProfile /></Guarded>} />
      <Route path="*" element={<Navigate to="/mira/home" replace />} />
    </Routes>
  </MiraAuthProvider>
);

export default MiraApp;
